import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import LogoHeader from "../../components/LogoHeader";
import Button from "../../components/Button";
import Header from "../../components/Header";
import { LoadingContainer } from "../../containers/loadingContainer";
import "./styles.css";
import { XrScienceMenuContainer } from "../../containers/XrScienceMenuContainer";
import { HandmolMenuContainer } from "../../containers/handmolMenuContainer";
import { PresenceIndicatorContainer } from "../../containers/presenceIndicatorContainer";

const animationDuration = 0.3;

const CreateView = ({
  isConnectedToASession,
  isLoading,
  isConnected,
  onTryAgain,
  onStartConnecting,
  isSetupError,
  errorMsg,
  isEstablishingConnection,
  connectionQualityFailed,
}) => {
  const [activeTab, setActiveTab] = useState("HANDMOL");
  const containerRef = useRef(null);
  const activeTabElementRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isConnected && !isEstablishingConnection) {
      onStartConnecting();
    }
  }, [isConnected, isEstablishingConnection]);

  useEffect(() => {
    if (connectionQualityFailed) {
      navigate("/");
    }
  }, [connectionQualityFailed]);

  useEffect(() => {
    if (isConnectedToASession) {
      navigate("/app");
    }
  }, [isConnectedToASession]);

  useEffect(() => {
    const container = containerRef.current;
    const activeTabElement = activeTabElementRef.current;

    if (activeTab && container && activeTabElement) {
      // 1. Define your desired padding in pixels
      const horizontalPadding = 20;
      const verticalPadding = 4;

      // 2. Get the full geometry of the active tab and its container
      const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = activeTabElement;
      const { offsetWidth: containerWidth, offsetHeight: containerHeight } = container;

      // 3. Calculate the new, larger dimensions
      const paddedLeft = offsetLeft - horizontalPadding;
      const paddedTop = offsetTop - verticalPadding;
      const paddedWidth = offsetWidth + horizontalPadding * 2;
      const paddedHeight = offsetHeight + verticalPadding * 2;

      // 4. Calculate the 'inset' percentages for the clip-path
      const clipTopPercent = (paddedTop / containerHeight) * 100;
      const clipRightPercent = 100 - ((paddedLeft + paddedWidth) / containerWidth) * 100;
      const clipBottomPercent = 100 - ((paddedTop + paddedHeight) / containerHeight) * 100;
      const clipLeftPercent = (paddedLeft / containerWidth) * 100;

      // 5. Apply the style with a large radius to ensure smooth corners
      container.style.clipPath = `inset(${clipTopPercent}% ${clipRightPercent}% ${clipBottomPercent}% ${clipLeftPercent}% round 999px)`;
    }
  }, [activeTab, isSetupError, isLoading, isEstablishingConnection]);

  useEffect(() => {
    if (isConnectedToASession) {
      navigate("/app");
    }
  }, [isConnectedToASession]);

  if (isLoading || isEstablishingConnection) {
    return <LoadingContainer />;
  }

  if (isSetupError) {
    return (
      <div className="setup-error-container column">
        <LogoHeader variant="dark" />
        <p className="setup-error-title">Simulation Setup Error</p>
        <div className="error-divider"></div>
        <p className="setup-error-msg">{errorMsg || "Something went wrong"}</p>
        <Button handleClick={() => onTryAgain()}>Try again</Button>
        <p className="setup-error-subtle">If error persists, please contact us.</p>
      </div>
    );
  }

  return (
    <>
      <PresenceIndicatorContainer />
      <div className="container column">
        <div>
          <Header />

          <h2 className="create-title">New Session</h2>
          <h3 className="create-subtitle">SELECT YOUR TYPE OF SESSION</h3>

          <div className="tabs-wrapper row">
            <ul className="tab-list row">
              <li
                ref={activeTab === "HANDMOL" ? activeTabElementRef : null}
                onClick={() => {
                  setActiveTab("HANDMOL");
                }}
                className="tab"
              >
                Handmol
              </li>
              <li
                ref={activeTab === "SCIENCE" ? activeTabElementRef : null}
                onClick={() => {
                  setActiveTab("SCIENCE");
                }}
                className="tab"
              >
                Science XR
              </li>
            </ul>

            <div aria-hidden className="clip-path-container" ref={containerRef}>
              <ul className="tab-list row tab-list-overlay">
                <li
                  onClick={() => {
                    setActiveTab("HANDMOL");
                  }}
                  className="tab tab-overlay"
                >
                  Handmol
                </li>
                <li
                  onClick={() => {
                    setActiveTab("SCIENCE");
                  }}
                  className="tab tab-overlay"
                >
                  Science XR
                </li>
              </ul>
            </div>
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            {activeTab === "HANDMOL" && (
              <motion.div
                key="handmol"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                // exit={{ opacity: 0, x: 40 }}
                transition={{ duration: animationDuration }}
              >
                <HandmolMenuContainer />
              </motion.div>
            )}
            {activeTab === "SCIENCE" && (
              <motion.div
                key="science"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                // exit={{ opacity: 0, x: -40 }}
                transition={{ duration: animationDuration }}
              >
                <XrScienceMenuContainer />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <p className="footer">
          © 2025 MolecularWebXR. All rights reserved. Part of the{" "}
          <a
            href="https://molecularweb.org"
            target="_blank"
            rel="noopener noreferrer"
            className="create-menu-text link"
          >
            Molecularweb.org
          </a>{" "}
          universe
        </p>
      </div>
    </>
  );
};

export default CreateView;
